import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import getLastPageVisitedByScreenIdList from 'common/util/getLastPageVisitedByScreenIdList';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  getSecurityDepositSettings,
} from 'common/listingHelpers';

import {
  SR_USER_GROUP_ROLE_PARTNER,
} from 'common/userRoles';

import {
  LISTING_AGREEMENT_MANUAL,
  LISTING_PROPERTY_OWNER_BUSINESS,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM,
} from 'common/listingConstants';

import {
  isApplicationAccepted,
} from 'common/householdApplication/applicationHelpers';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_LISTINGS,
  DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS,
  DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS,
} from 'common/dataTableConstants';

import {
  SCREEN_ID_PARTNER_OVERVIEW,
  SCREEN_ID_PARTNER_LISTING_HOUSEHOLD_APPROVAL,
} from 'client/constants/screenIds';

import {
  SR_API_ACTION_HOUSEHOLD_LISTING_INCREASE_SECURITY_DEPOSIT,
  SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_COLLECT_RENT,
  SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_LEASING_INFO,
  SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_ACCEPTED,
} from 'api/actionTypes';

import {
  dataTable,
  actionDataTableReset,
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import branch from 'client/core/branch';
import allowedRoles, {
  getUserLandingPage,
} from 'client/helpers/allowedRoles';
import withScreenId from 'client/helpers/withScreenId';
import withApiState from 'client/helpers/withApiState';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withTransitionHook from 'client/helpers/withTransitionHook';

import postWithState from 'client/actions/postWithState';
import PartnerActions from 'client/actions/Partner';
import HouseholdApplications from 'client/actions/Household';
import AccountManagerActions from 'client/actions/AccountManager';
import MarketActions from 'client/actions/Market';

import {
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrPartnerApprovalForm from 'client/components/SrPartnerApproval/SrPartnerApprovalForm';
import SrPartnerContactSupportModal from 'client/components/SrPartnerContactSupportModal';
import { SR_CONTACT_CARD_HEADLINE_TYPE_BOLD } from 'client/components/SrContactCard/SrContactCard';

function getHouseholdListingDataTableSettings({ match }) {
  const householdListingId = parseInt(match.params.householdListingId, 10);

  const dataTableSettings = {
    itemsPropName: 'household',
    dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_LISTINGS,
    viewId: `by-household-listing-id-${householdListingId}`,
    count: 1,
    injectSingleItem: true,
    customParams: {
      householdListings: {
        householdListingId,
      },
    },
  };

  return dataTableSettings;
}

@withTransitionHook
@allowedRoles({
  roles: SR_USER_GROUP_ROLE_PARTNER,
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_LISTING_HOUSEHOLD_APPROVAL)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  accountManager: ['accountManagers', 'byListingId', match.params.listingId],
  screenIdHistory: ['analytics', 'screenIdHistory'],
  pathHistory: ['analytics', 'pathHistory'],
  marketsById: ['markets', 'byId'],
}))
@dataTable(getHouseholdListingDataTableSettings)
@withApiState(() => ({
  householdListingIncreaseSecurityDepositApiState: {
    action: SR_API_ACTION_HOUSEHOLD_LISTING_INCREASE_SECURITY_DEPOSIT,
  },
  householdListingUpdateCollectRentApiState: {
    action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_COLLECT_RENT,
  },
  householdListingUpdateLeasingInfoApiState: {
    action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_LEASING_INFO,
  },
  householdMarkApplicationsAcceptedApiState: {
    action: SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_ACCEPTED,
  },
}))
class PartnerListingHouseholdApprovalPage extends Component {
  static propTypes = {
    listing: PropTypes.object,
    partner: PropTypes.object,
    accountManager: PropTypes.object,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    isTransitioning: PropTypes.bool.isRequired,
    screenIdHistory: PropTypes.array.isRequired,
    pathHistory: PropTypes.array.isRequired,
    householdListingIncreaseSecurityDepositApiState: PropTypes.object.isRequired,
    householdListingUpdateCollectRentApiState: PropTypes.object.isRequired,
    householdListingUpdateLeasingInfoApiState: PropTypes.object.isRequired,
    householdMarkApplicationsAcceptedApiState: PropTypes.object.isRequired,
    marketsById: PropTypes.object,
  }

  static getPageMetadata = () => ({
    pageTitle: 'Partner Listing | Approve Household',
  })

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const listingId = parseInt(match.params.listingId, 10);
    const promises = [];

    if (!partner) {
      promises.push(PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }));
    }

    if (listingId) {
      promises.push(
        AccountManagerActions.getByListingId(inject, { listingId }),
        MarketActions.getList(inject),
      );
    }

    const householdListingDataTableSettings = getHouseholdListingDataTableSettings({ match });
    actionInitializeDataTableState(inject, householdListingDataTableSettings);
    promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, householdListingDataTableSettings));

    return Promise.all(promises);
  }

  constructor() {
    super();

    this.state = {
      shouldShowSavedModal: false,
      shouldShowContactSupportModal: false,
    };
  }

  _reloadDataTable = () => {
    const {
      dispatch,
      match,
    } = this.props;

    return dispatch(actionDataTableLoadItems, getHouseholdListingDataTableSettings({
      match,
    }));
  }

  _onGoBack = () => {
    const {
      partner,
      household,
      history,
      screenIdHistory,
      pathHistory,
    } = this.props;

    const lastPageVisited = getLastPageVisitedByScreenIdList({
      screenIdHistory,
      pathHistory,
      screenIdList: [
        SCREEN_ID_PARTNER_OVERVIEW,
      ],
    });

    const redirectUrl = lastPageVisited?.screenId === SCREEN_ID_PARTNER_OVERVIEW
      ? `/partner/${partner.slug}`
      : `/partner/${partner.slug}/listing/${household.listing.id}`;

    history.push(redirectUrl);
  }

  _onStepChange = ({ stepDefinition }) => {
    const {
      partner,
      household,
      history,
      location,
    } = this.props;

    const {
      listing,
      householdListing,
    } = household;

    const newPathname = `/partner/${partner.slug}/listing/${listing.id}/approve/${householdListing.id}/${stepDefinition.id}`; // eslint-disable-line max-len
    if (location.pathname !== newPathname) {
      history.replace(newPathname);
    }
  }

  _openShowContactSupportModal = () => {
    this.setState({ shouldShowContactSupportModal: true });
  }

  _onSubmit = (values, { stepDefinition, formikBag, goToNextStep }) => {
    const {
      partner,
      household,
      match,
      dispatch,
    } = this.props;

    const {
      listing,
      householdListing,
      householdApplications,
      listingStripeAccount,
      partnerStripeAccount,
    } = household;

    switch (stepDefinition.id) {
      case 'security-deposit': {
        const payload = {
          householdListingId: householdListing.id,
          securityDepositIncreaseMultiplier: values.securityDepositIncreaseMultiplier,
          securityDepositIncreaseReason: values.securityDepositIncreaseReason,
        };

        if (payload.securityDepositIncreaseMultiplier === 1) {
          payload.securityDepositIncreaseReason = null;
        }

        dispatch(postWithState, {
          action: SR_API_ACTION_HOUSEHOLD_LISTING_INCREASE_SECURITY_DEPOSIT,
          payload: payload,
          onSuccess: () => {
            this._reloadDataTable().then(goToNextStep);
          },
        });

        break;
      }
      case 'collect-rent': {
        dispatch(postWithState, {
          action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_COLLECT_RENT,
          payload: setNullEmptyValues({
            rentPrice: values.rentPrice,
            householdListingId: householdListing.id,
            collectFirstMonthTime: values.collectFirstMonthTime,
            collectFirstMonth: values.collectFirstMonth,
            collectPetRent: partner.experienceConfiguration.collectPetRent,
            collectPetDeposit: partner.experienceConfiguration.collectPetDeposit,
            collectPetFee: partner.experienceConfiguration.collectPetFee,
          }),
          onSuccess: () => {
            this._reloadDataTable().then(goToNextStep);
          },
        });
        break;
      }
      case 'listing-agreement':
      case 'property-owner':
      case 'rent-payments':
      case 'extra-notes':
      case 'addenda': {
        const payload = {
          householdListingId: householdListing.id,
          listingAgreementType: values.listingAgreementType,
          listingAgreementUrl: values.listingAgreementUrl,
          propertyOwnerType: values.propertyOwnerType,
          propertyOwnerName: values.propertyOwnerName,
          propertyOwnerPhone: values.propertyOwnerPhone,
          propertyOwnerEmail: values.propertyOwnerEmail,
          isManagementSameAsPropertyOwner: values.isManagementSameAsPropertyOwner,
          managementName: values.managementName,
          managementPhone: values.managementPhone,
          managementEmail: values.managementEmail,
          isRepairSameAsManagement: values.isRepairSameAsManagement,
          repairContactName: values.repairContactName,
          repairContactPhone: values.repairContactPhone,
          repairContactEmail: values.repairContactEmail,
          whoCollectsRentPayment: values.whoCollectsRentPayment,
          rentPaymentDescription: values.rentPaymentDescription,
          additionalAddenda: values.additionalAddenda,
          partnerLeaseNotes: values.partnerLeaseNotes,
        };

        if (
          payload.isManagementSameAsPropertyOwner
          || payload.propertyOwnerType === LISTING_PROPERTY_OWNER_BUSINESS
        ) {
          payload.managementName = payload.propertyOwnerName;
          payload.managementPhone = payload.propertyOwnerPhone;
          payload.managementEmail = payload.propertyOwnerEmail;
          formikBag.setFieldValue('managementName', payload.propertyOwnerName);
          formikBag.setFieldValue('managementPhone', payload.propertyOwnerPhone);
          formikBag.setFieldValue('managementEmail', payload.propertyOwnerEmail);
        }

        if (payload.isRepairSameAsManagement) {
          payload.repairContactName = payload.managementName;
          payload.repairContactPhone = payload.managementPhone;
          payload.repairContactEmail = payload.managementEmail;
          formikBag.setFieldValue('repairContactName', payload.managementName);
          formikBag.setFieldValue('repairContactPhone', payload.managementPhone);
          formikBag.setFieldValue('repairContactEmail', payload.managementEmail);
        }

        if (payload.listingAgreementType === LISTING_AGREEMENT_MANUAL) {
          payload.listingAgreementUrl = null;
        }

        dispatch(postWithState, {
          action: SR_API_ACTION_HOUSEHOLD_LISTING_UPDATE_LEASING_INFO,
          payload: setNullEmptyValues(payload),
          onSuccess: () => {
            this._reloadDataTable().then(goToNextStep);
          },
        });
        break;
      }
      case 'summary': {
        const householdIsApproved = householdApplications.every(isApplicationAccepted);
        if (householdIsApproved) {
          this._onGoBack();
          return;
        }

        const {
          whoCollectsSecurityDepositPetFees,
          securityDepositCollectionInstructions,
        } = getSecurityDepositSettings({
          listing,
          partner,
        });

        const payload = {
          householdListingId: householdListing.id,
          whoCollectsSecurityDepositPetFees: whoCollectsSecurityDepositPetFees,
          securityDepositCollectionInstructions: null,
          srStripeAccountId: null,
        };

        if (whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL) {
          payload.securityDepositCollectionInstructions = securityDepositCollectionInstructions;
        }
        else if (whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM) {
          const stripeAccount = (listingStripeAccount || partnerStripeAccount);
          payload.srStripeAccountId = stripeAccount?.id;
        }

        dispatch(HouseholdApplications.approveApplications, payload).then(() => {
          return Promise.all([
            dispatch(actionDataTableReset, {
              dataSource: DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS,
            }),
            dispatch(
              actionDataTableLoadItems,
              getHouseholdListingDataTableSettings({ match })
            ),
            dispatch(PartnerActions.getHouseholdsApplyingToListing, {
              listingId: householdListing.listingId,
              forceRefresh: true,
            }),
            dispatch(actionDataTableReset, {
              dataSource: DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS,
            }),
          ]);
        }).then(() => {
          this.setState({
            shouldShowSavedModal: true,
          });
        });
        break;
      }
      default: {
        throw new Error(`Handler not found for ${stepDefinition.id}`);
      }
    }
  }

  render() {
    const {
      dispatch,
      partner,
      accountManager,
      household,
      currentUser,
      marketsById,
      location,
      history,
      isTransitioning,
      householdListingIncreaseSecurityDepositApiState,
      householdListingUpdateCollectRentApiState,
      householdListingUpdateLeasingInfoApiState,
      householdMarkApplicationsAcceptedApiState,
    } = this.props;

    const {
      shouldShowSavedModal,
      shouldShowContactSupportModal,
    } = this.state;

    if (isTransitioning && (!partner || !household)) {
      return (<div className='loading-gif' />);
    }

    if (!partner || !household) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    const {
      listing,
      concession,
      landlord,
      householdListing,
      householdApplications,
      householdGuarantors,
      listingStripeAccount,
      partnerStripeAccount,
    } = household;

    return (
      <>
        <SrPartnerApprovalForm
          partner={partner}
          listing={listing}
          concession={concession}
          market={(marketsById || {})[listing?.marketId] || null}
          currentUser={currentUser}
          landlord={landlord}
          householdListing={householdListing}
          householdApplications={householdApplications}
          householdGuarantors={householdGuarantors}
          listingStripeAccount={listingStripeAccount}
          partnerStripeAccount={partnerStripeAccount}
          location={location}
          history={history}
          isBusy={
            householdListingIncreaseSecurityDepositApiState.inProgress
            || householdListingUpdateCollectRentApiState.inProgress
            || householdListingUpdateLeasingInfoApiState.inProgress
            || householdMarkApplicationsAcceptedApiState.inProgress
          }
          dispatch={dispatch}
          onBack={this._onGoBack}
          onSubmit={this._onSubmit}
          onStepChange={this._onStepChange}
          openShowContactSupportModal={this._openShowContactSupportModal}
        />
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl='/images/checkmark-circle-large-green.svg'
            headline='Application Approved!'
            underline
            message='Sunroom will send the payment instructions to the renter.'
            maxWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            buttonText='Ok thanks!'
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            onButtonClick={this._onGoBack}
          />
        )}
        {shouldShowContactSupportModal && (
          <SrPartnerContactSupportModal
            accountManager={{
              accountManagerName: accountManager?.name,
              accountManagerPhone: accountManager?.phone,
              accountManagerEmail: accountManager?.email,
              accountManagerProfileImage: accountManager?.profileImage,
            }}
            onClose={() => this.setState({ shouldShowContactSupportModal: false })}
            headline={
              'Processing for this listing has already begun. ' +
              'To edit the approval details please contact your Account Manager'
            }
            headlineType={SR_CONTACT_CARD_HEADLINE_TYPE_BOLD}
          />
        )}
      </>
    );
  }
}

PartnerListingHouseholdApprovalPage.displayName = 'PartnerListingHouseholdApprovalPage';

export default PartnerListingHouseholdApprovalPage;
